import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import { TaxonFilterField } from 'components/datatable/SimpleSelectionTable'
import { groupBy, intersectionWith, isNil, isUndefined, orderBy, uniqBy } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { normalizeString } from 'utils/StringUtil'
import PropTypes from 'prop-types'
import { nbPerPageLabel, nbPerPageLabelMedium } from 'referencial/constants/ReferencialConstants'
import useListIndexed from 'utils/customHook/useListIndexed'
import NumberField from 'components/forms/NumberField'
import useSandreList from 'utils/customHook/useSandreList'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Select from 'components/forms/Select'
import useBoolean from 'utils/customHook/useBoolean'
import { CardTable, NewTable } from 'components/datatable/NewTable'
import { THRESHOLD_DISCRIMINANT, THRESHOLD_DISPLAY, THRESHOLD_TYPE } from 'utils/QualityThresholdUtils'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import SliderPanel from 'components/slider/SliderPanel'
import { JOB_COLOR, whiteColor } from 'utils/constants/ColorTheme'

const PopinTaxonThreshold = ({
    isOpen = false,
    fullyUpdatable = false,
    taxonThreshold = {},
    validate = () => {},
    close = () => {},
}) => {
    const {
        taxons,
        qualitometers,
    } = useSelector(store => ({
        taxons: store.TaxonReducer.taxons,
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const indexedTaxons = useListIndexed(taxons)

    const lexiconCalcium = useSandreList('CALCIUM')
    const lexiconFRType = useSandreList('QUALITOMETRES.TYPE_FR.RIVIERE')
    const lexiconFishContext = useSandreList('QUALITOMETRES.CONTEXTEPISCICOLE')

    const [parameterCode, setParameterCode] = useState()
    const [station, setStation] = useState()

    const [threshold1, setThreshold1] = useState()
    const [threshold2, setThreshold2] = useState()
    const [threshold3, setThreshold3] = useState()
    const [threshold4, setThreshold4] = useState()

    const [calciumClass, setCalciumClass] = useState()
    const [frType, setFrType] = useState()
    const [fishCode, setFishCode] = useState()

    const [thresholdType, setThresholdType] = useState()
    const [display, setDisplay] = useState()
    const [discriminant, setDiscriminant] = useState()

    useEffect(() => {
        if (isOpen) {
            setParameterCode(taxonThreshold.parameterCode)
            setStation(taxonThreshold.station)

            setThreshold1(taxonThreshold.threshold1)
            setThreshold2(taxonThreshold.threshold2)
            setThreshold3(taxonThreshold.threshold3)
            setThreshold4(taxonThreshold.threshold4)

            setCalciumClass(taxonThreshold.calciumClass)
            setFrType(taxonThreshold.frType)
            setFishCode(taxonThreshold.fishCode)

            setThresholdType(taxonThreshold.thresholdType ?? THRESHOLD_TYPE.INCREASING)
            setDisplay(taxonThreshold.display ?? THRESHOLD_DISPLAY.INCREASING)
            setDiscriminant(taxonThreshold.discriminant ?? THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL)
        }
    }, [isOpen, taxonThreshold.calciumClass, taxonThreshold.discriminant, taxonThreshold.display, taxonThreshold.fishCode, taxonThreshold.frType, taxonThreshold.parameterCode, taxonThreshold.station, taxonThreshold.threshold1, taxonThreshold.threshold2, taxonThreshold.threshold3, taxonThreshold.threshold4, taxonThreshold.thresholdType, taxonThreshold.unit])

    useEffect(() => {
        if (isUndefined(threshold1)) {
            setThreshold2(undefined)
            setThreshold3(undefined)
            setThreshold4(undefined)
        }
    }, [threshold1])

    useEffect(() => {
        if (isUndefined(threshold2)) {
            setThreshold3(undefined)
            setThreshold4(undefined)
        }
    }, [threshold2])

    useEffect(() => {
        if (isUndefined(threshold3)) {
            setThreshold4(undefined)
        }
    }, [threshold3])

    const onChangeThresholdType = newType => {
        setThresholdType(newType)
        if (newType === THRESHOLD_TYPE.BOUNDED) {
            setDiscriminant(THRESHOLD_DISCRIMINANT.INF_OR_SUP_OR_EQUAL)
            setDisplay(THRESHOLD_DISPLAY.INCREASING)
            setThreshold3()
            setThreshold4()
            if (thresholdType === THRESHOLD_TYPE.DECREASING) {
                setThreshold1(threshold2)
                setThreshold2(threshold1)
            }
        }
        if (newType === THRESHOLD_TYPE.INCREASING) {
            setDiscriminant(THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL)
            if (thresholdType === THRESHOLD_TYPE.DECREASING) {
                const [th1, th2, th3, th4] = [threshold1, threshold2, threshold3, threshold4].filter(th => !isNil(th)).toReversed()
                setThreshold1(th1)
                setThreshold2(th2)
                setThreshold3(th3)
                setThreshold4(th4)
            }
        }
        if (newType === THRESHOLD_TYPE.DECREASING) {
            setDiscriminant(THRESHOLD_DISCRIMINANT.INF_OR_EQUAL)
            const [th1, th2, th3, th4] = [threshold1, threshold2, threshold3, threshold4].filter(th => !isNil(th)).toReversed()
            setThreshold1(th1)
            setThreshold2(th2)
            setThreshold3(th3)
            setThreshold4(th4)
        }
    }

    const thresholdTypes = useMemo(() => [
        { id: THRESHOLD_TYPE.INCREASING, name: 'Croissant' },
        { id: THRESHOLD_TYPE.DECREASING, name: 'Décroissant' },
        { id: THRESHOLD_TYPE.BOUNDED, name: 'Borné' },
        // { id: THRESHOLD_TYPE.INVERTED_BOUNDED, name: 'Borné inversé' },
    ], [])

    const displayModes = useMemo(() => [
        { id: THRESHOLD_DISPLAY.INCREASING, name: 'Croissant' },
        { id: THRESHOLD_DISPLAY.DECREASING, name: 'Décroissant' },
    ], [])

    const increasingDiscriminants = useMemo(() => [
        { id: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL, name: `x >= ${i18n.threshold}` },
        { id: THRESHOLD_DISCRIMINANT.STRICTLY_SUP, name: `x > ${i18n.threshold}` },
    ], [])

    const decreasingDiscriminants = useMemo(() => [
        { id: THRESHOLD_DISCRIMINANT.INF_OR_EQUAL, name: `x <= ${i18n.threshold}` },
        { id: THRESHOLD_DISCRIMINANT.STRICTLY_INF, name: `x < ${i18n.threshold}` },
    ], [])

    const boundedDiscriminants = useMemo(() => [
        { id: THRESHOLD_DISCRIMINANT.INF_OR_SUP_OR_EQUAL, name: `${i18n.threshold} 1 <= x ${i18n.and} ${i18n.threshold} 2 >= x` },
        { id: THRESHOLD_DISCRIMINANT.STRICTLY_INF_OR_SUP, name: `${i18n.threshold} 1 < x ${i18n.and} ${i18n.threshold} 2 > x` },
    ], [])

    const usedDiscriminants = useMemo(() => {
        if (thresholdType === THRESHOLD_TYPE.INCREASING) return increasingDiscriminants
        if (thresholdType === THRESHOLD_TYPE.DECREASING) return decreasingDiscriminants
        if (thresholdType === THRESHOLD_TYPE.BOUNDED) return boundedDiscriminants
        return []
    }, [boundedDiscriminants, decreasingDiscriminants, increasingDiscriminants, thresholdType])

    return (
        <Dialog
            maxWidth='lg'
            fullWidth
            open={isOpen}
            slot={{
                parper: {
                    minHeight: undefined,
                    maxHeight: undefined,
                },
            }}
        >
            <DefaultDialogTitle
                title={i18n.threshold}
                onClose={close}
            />
            <DialogContent style={{ overflowX: 'hidden' }}>
                <Grid container spacing={1} direction='column'>
                    <Grid>
                        <Card>
                            <CardContent>
                                <Grid container columnSpacing={2} rowSpacing={1} alignItems='center' style={{ paddingTop: '5px' }}>
                                    <Grid size={6}>
                                        <Select
                                            label={i18n.taxon}
                                            value={parameterCode}
                                            onChange={setParameterCode}
                                            options={taxons}
                                            disabled={!fullyUpdatable}
                                            keyLabel='latinName'
                                        />
                                    </Grid>
                                    <Grid size={6} />
                                    <Grid size={12}>
                                        <Select
                                            label={i18n.station}
                                            value={station}
                                            onChange={setStation}
                                            options={qualitometers}
                                            disabled={!fullyUpdatable}
                                            keyValue='id'
                                            keyLabel='displayLabel'
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <SuperMultiAutocomplete
                                            label={'Type de seuil'}
                                            values={thresholdType}
                                            onChange={onChangeThresholdType}
                                            options={thresholdTypes}
                                            noNullValue
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <SuperMultiAutocomplete
                                            label={'Mode d\'affichage'}
                                            values={display}
                                            onChange={setDisplay}
                                            options={displayModes}
                                            disabled={thresholdType === THRESHOLD_TYPE.BOUNDED}
                                            noNullValue
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <SuperMultiAutocomplete
                                            label={'Discriminant'}
                                            values={discriminant}
                                            onChange={setDiscriminant}
                                            options={usedDiscriminants}
                                            disabled={thresholdType === THRESHOLD_TYPE.BOUNDED}
                                            noNullValue
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <NumberField
                                            title={i18n.threshold1}
                                            value={threshold1}
                                            floatValue
                                            obligatory
                                            onChange={setThreshold1}
                                            min={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold2 : undefined}
                                            max={thresholdType === THRESHOLD_TYPE.DECREASING ? undefined : threshold2}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        {
                                            (!isUndefined(threshold1) || thresholdType === THRESHOLD_TYPE.BOUNDED) && (
                                                <NumberField
                                                    title={i18n.threshold2}
                                                    value={threshold2}
                                                    floatValue
                                                    onChange={setThreshold2}
                                                    obligatory={thresholdType === THRESHOLD_TYPE.BOUNDED}
                                                    min={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold3 : threshold1}
                                                    max={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold1 : threshold3}
                                                />
                                            )
                                        }
                                    </Grid>
                                    <Grid size={3}>
                                        {
                                            !isUndefined(threshold2) && thresholdType !== THRESHOLD_TYPE.BOUNDED && (
                                                <NumberField
                                                    title={i18n.threshold3}
                                                    value={threshold3}
                                                    floatValue
                                                    onChange={setThreshold3}
                                                    min={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold4 : threshold2}
                                                    max={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold2 : threshold4}
                                                />
                                            )
                                        }
                                    </Grid>
                                    <Grid size={3}>
                                        {
                                            !isUndefined(threshold3) && thresholdType !== THRESHOLD_TYPE.BOUNDED && (
                                                <NumberField
                                                    title={i18n.threshold4}
                                                    value={threshold4}
                                                    floatValue
                                                    onChange={setThreshold4}
                                                    min={thresholdType === THRESHOLD_TYPE.DECREASING ? undefined : threshold3}
                                                    max={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold3 : undefined}
                                                />
                                            )
                                        }
                                    </Grid>
                                    <Grid size={3}>
                                        <Select
                                            label={i18n.hardness}
                                            value={calciumClass}
                                            options={lexiconCalcium}
                                            onChange={setCalciumClass}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <Select
                                            label={i18n.frType}
                                            value={frType}
                                            options={lexiconFRType}
                                            onChange={setFrType}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <Select
                                            label={i18n.fishContext}
                                            value={fishCode}
                                            options={lexiconFishContext}
                                            onChange={setFishCode}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    {!isNil(threshold1) && (thresholdType !== THRESHOLD_TYPE.BOUNDED || !isNil(threshold2)) && (
                        <Grid>
                            <Card>
                                <CardContent>
                                    <SliderPanel
                                        title={indexedTaxons[parameterCode]?.latinName ?? i18n.defaultThreshold}
                                        analysis={{ result: 0, remark: '1' }}
                                        threshold={{
                                            thresholdType,
                                            display,
                                            discriminant,
                                            threshold1,
                                            threshold2,
                                            threshold3,
                                            threshold4,
                                        }}
                                        withThresholdLevels
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Grid container alignItems='center' justifyContent='right' columnSpacing={2} style={{ padding: '0 10' }}>
                    <Grid container size={3} justifyContent='flex-end'>
                        <Button
                            onClick={() => {
                                validate({
                                    parameterCode,
                                    station,
                                    threshold1,
                                    threshold2,
                                    threshold3,
                                    threshold4,
                                    calciumClass,
                                    frType,
                                    fishCode,
                                    thresholdType,
                                    display,
                                    discriminant,
                                })
                            }}
                            variant='contained'
                            color='primary'
                            disabled={isUndefined(threshold1)}
                        >
                            {i18n.validate}
                        </Button>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    )
}

PopinTaxonThreshold.propTypes = {
    isOpen: PropTypes.bool,
    fullyUpdatable: PropTypes.bool,
    threshold: PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        comment: PropTypes.string,
        status: PropTypes.number,
    }),
    taxonThreshold: PropTypes.shape({
        parameterCode: PropTypes.string,
        station: PropTypes.number,
        threshold1: PropTypes.number,
        threshold2: PropTypes.number,
        threshold3: PropTypes.number,
        threshold4: PropTypes.number,
        calciumClass: PropTypes.number,
        frType: PropTypes.number,
        fishCode: PropTypes.number,
    }),
    validate: PropTypes.func,
    close: PropTypes.func,
}

const StepTaxonThresholds = ({
    qualityThresholds = [],
    setQualityThresholds = () => {},
}) => {
    const {
        taxons,
        qualitometers,
    } = useSelector(store => ({
        taxons: store.TaxonReducer.taxons,
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const indexedTaxons = useListIndexed(taxons, 'code')
    const indexedQualitometers = useListIndexed(qualitometers, 'id')

    const lexiconCalcium = useSandreList('CALCIUM')
    const lexiconFRType = useSandreList('QUALITOMETRES.TYPE_FR.RIVIERE')
    const lexiconFishContext = useSandreList('QUALITOMETRES.CONTEXTEPISCICOLE')

    const [filter, setFilter] = useState({})
    const [selectedIndex, setSelectedIndex] = useState()
    const {
        value: isOpen,
        setTrue: open,
        setFalse: close,
    } = useBoolean(false)

    const selectedThreshold = useMemo(() => {
        return qualityThresholds[selectedIndex]
    }, [qualityThresholds, selectedIndex])

    const groupThresholds= groupBy(qualityThresholds, th => `${th.parameterCode}:${th.unit}:${th.station}`)

    const formattedThresholds = useMemo(() => {
        return qualityThresholds.map((th, index) => {
            const key = `${th.parameterCode}:${th.unit}:${th.station}`
            const taxon = indexedTaxons[th.parameterCode]
            const taxonDisplay = taxon?.latinName

            return {
                code: th.parameterCode,
                taxon: isUndefined(th.parameterCode) ? i18n.defaultThreshold : taxonDisplay,
                station: indexedQualitometers[th.station]?.displayLabel,
                threshold1: th.threshold1,
                threshold2: th.threshold2,
                threshold3: th.threshold3,
                threshold4: th.threshold4,
                hardness: lexiconCalcium.find(l => l.code === th.calciumClass)?.name,
                frType: lexiconFRType.find(l => l.code === th.frType)?.name,
                fishContext: lexiconFishContext.find(l => l.code === th.fishCode)?.name,
                color: groupThresholds[key].length > 1 ? JOB_COLOR.RED : whiteColor,
                index,
                searchValue: [taxon?.name, taxon?.shortLabel, taxon?.code].map((label = '') => normalizeString(label)),
            }
        })
    }, [groupThresholds, indexedQualitometers, indexedTaxons, lexiconCalcium, lexiconFRType, lexiconFishContext, qualityThresholds])


    const filteredThresholds = useMemo(() => {
        const { selection = '-1', listTaxon, searchValue } = filter
        const filterSelection = selection !== '-1' ? intersectionWith(formattedThresholds, listTaxon, (a, code) => a.code === code) : formattedThresholds
        const searchValueFormat = normalizeString(searchValue)
        return searchValue ? filterSelection.filter(p => p.searchValue.some(v => v.includes(searchValueFormat))) : filterSelection
    }, [filter, formattedThresholds])

    const actions = [{
        icon: 'note_add',
        tooltip: i18n.add,
        onClick: () => {
            setSelectedIndex()
            open()
        },
    }, {
        icon: 'info',
        tooltip: i18n.redLineMeanDuplicate,
    }]

    const nbThresholds = qualityThresholds.length
    const nbTaxons = uniqBy(qualityThresholds, 'parameterCode').length

    return (
        <Grid container justifyContent='center' rowSpacing={1}>
            <Grid size={12}>
                <StyledFieldSet>
                    <StyledLegend>{i18n.filter}</StyledLegend>
                    <TaxonFilterField
                        filter={filter}
                        setFilter={setFilter}
                    />
                </StyledFieldSet>
            </Grid>
            <Grid size={12}>
                <CardTable
                    title={`${nbThresholds} ${nbThresholds > 1 ? i18n.thresholds : i18n.threshold}, ${nbTaxons} ${nbTaxons > 1 ? i18n.taxons : i18n.taxon}`}
                    actions={actions}
                    hideNbElements

                    headers={['taxon', 'station', 'threshold1', 'threshold2', 'threshold3', 'threshold4', 'hardness', 'frType', 'fishContext']}
                    rows={orderBy(filteredThresholds, 'taxon')}
                    rowsPerPageOptions={nbPerPageLabel}

                    lineActions={[
                        {
                            icon: 'delete',
                            onClick: ({ index }) => setQualityThresholds(list => list.filter((_, i) => i !== index)),
                        },
                        {
                            icon: 'edit',
                            onClick: ({ index }) => {
                                setSelectedIndex(index)
                                open()
                            },
                        },
                        {
                            icon: 'queue',
                            onClick: ({ index }) => setQualityThresholds(list => [...list, qualityThresholds[index]]),
                        },
                    ]}
                />
                <PopinTaxonThreshold
                    isOpen={isOpen}
                    fullyUpdatable
                    taxonThreshold={selectedThreshold}
                    validate={newThreshold => {
                        setQualityThresholds(list => {
                            if (isUndefined(selectedIndex)) {
                                return [...list, newThreshold]
                            }
                            return list.map((th, i) => i === selectedIndex ? newThreshold : th)
                        })
                        close()
                    }}
                    close={close}
                />
            </Grid>
        </Grid>
    )
}

StepTaxonThresholds.propTypes = {
    threshold: PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        comment: PropTypes.string,
        status: PropTypes.number,
    }),
    qualityThresholds: PropTypes.arrayOf(PropTypes.shape({
        parameterCode: PropTypes.string,
        unit: PropTypes.string,
        station: PropTypes.number,
        threshold1: PropTypes.number,
        threshold2: PropTypes.number,
        threshold3: PropTypes.number,
        threshold4: PropTypes.number,
        calciumClass: PropTypes.number,
        frType: PropTypes.number,
        fishCode: PropTypes.number,
    })),
    setQualityThresholds: PropTypes.func,
}

const TableTaxonThreshold = ({
    threshold = {},
    qualityThresholds = [],
    setQualityThresholds = () => {},
    isEditMode = false,
}) => {
    const {
        taxons,
        qualitometers,
    } = useSelector(store => ({
        taxons: store.TaxonReducer.taxons,
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const taxonsIndexed = useListIndexed(taxons, 'code')
    const indexedQualitometers = useListIndexed(qualitometers, 'id')

    const lexiconCalcium = useSandreList('CALCIUM')
    const lexiconFRType = useSandreList('QUALITOMETRES.TYPE_FR.RIVIERE')
    const lexiconFishContext = useSandreList('QUALITOMETRES.CONTEXTEPISCICOLE')

    const [selectedIndex, setSelectedIndex] = useState()
    const {
        value: isOpen,
        setTrue: open,
        setFalse: close,
    } = useBoolean(false)

    const selectedThreshold = useMemo(() => {
        return qualityThresholds[selectedIndex]
    }, [qualityThresholds, selectedIndex])

    const thresholdsFormatted = useMemo(() => {
        return qualityThresholds.map((th, index) => ({
            code: th.parameterCode,
            taxon: taxonsIndexed[th.parameterCode]?.latinName ?? '',
            station: indexedQualitometers[th.station]?.displayLabel,
            threshold1: th.threshold1,
            threshold2: th.threshold2,
            threshold3: th.threshold3,
            threshold4: th.threshold4,
            hardness: lexiconCalcium.find(l => l.code === th.calciumClass)?.name,
            frType: lexiconFRType.find(l => l.code === th.frType)?.name,
            fishContext: lexiconFishContext.find(l => l.code === th.fishCode)?.name,
            index,
        }))
    }, [qualityThresholds, taxonsIndexed, indexedQualitometers, lexiconCalcium, lexiconFRType, lexiconFishContext])

    return (
        <>
            <NewTable
                rows={thresholdsFormatted}
                headers={['taxon', 'station', 'threshold1', 'threshold2', 'threshold3', 'threshold4', 'hardness', 'frType', 'fishContext']}
                rowsPerPageOptions={nbPerPageLabelMedium}

                lineActions={[
                    {
                        icon: 'delete',
                        onClick: ({ index }) => setQualityThresholds(threshold.code, qualityThresholds.filter((_, i) => i !== index)),
                        displayed: isEditMode,
                    },
                    {
                        icon: 'edit',
                        onClick: ({ index }) => {
                            setSelectedIndex(index)
                            open()
                        },
                        displayed: isEditMode,
                    },
                ]}
            />
            <PopinTaxonThreshold
                isOpen={isOpen}
                taxonThreshold={selectedThreshold}
                validate={newThreshold => {
                    const newQualityThresholds = qualityThresholds.map((th, i) => i === selectedIndex ? newThreshold : th)
                    setQualityThresholds(threshold.code, newQualityThresholds)
                    close()
                }}
                close={close}
            />
        </>
    )
}

TableTaxonThreshold.propTypes = {
    threshold: PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        comment: PropTypes.string,
        status: PropTypes.number,
    }),
    qualityThresholds: PropTypes.arrayOf(PropTypes.shape({
        parameterCode: PropTypes.string,
        station: PropTypes.number,
        threshold1: PropTypes.number,
        threshold2: PropTypes.number,
        threshold3: PropTypes.number,
        threshold4: PropTypes.number,
        calciumClass: PropTypes.number,
        frType: PropTypes.number,
        fishCode: PropTypes.number,
    })),
    setQualityThresholds: PropTypes.func,
    isEditMode: PropTypes.bool,
}

export {
    PopinTaxonThreshold,
    TableTaxonThreshold,
    StepTaxonThresholds,
}